import React, { useMemo, useState } from 'react';
import { Tooltip, Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const getHighlightStylesFromSeverity = (severity) => {
    const ratio = (Math.max(1, Math.min(10, severity)) - 1) / 9;
    const startColor = { h: 215, s: 58, l: 91 };
    const endColor = { h: 210, s: 28, l: 25 };
    const lightness = startColor.l - (startColor.l - endColor.l) * ratio;
    const saturation = startColor.s - (startColor.s - endColor.s) * ratio;
    const backgroundColor = `hsla(${startColor.h}, ${saturation}%, ${lightness}%, 0.85)`;
    const textColor = lightness < 65 ? '#f0f0f0' : '#333';
    return { backgroundColor, color: textColor };
};

const InteractiveTextHighlight = ({ text, regions, infoPoint, verifyUrl, shieldIcon }) => {
    const { t } = useTranslation();
    const [hasFocus, setHasFocus] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const segments = useMemo(() => {
        if (!regions || regions.length === 0 || !text) {
            return [<span key="full-text">{text}</span>];
        }

        const validSpans = regions
            .map((region, idx) => {
                const { quote, reason, severity } = region;
                if (!quote) return null;
                const startIndex = text.indexOf(quote);
                if (startIndex === -1) return null;
                return {
                    originalIndex: idx, 
                    start: startIndex,
                    end: startIndex + quote.length,
                    reason,
                    severity: severity || 5,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.start - b.start);

        const jsxSegments = [];
        let lastIndex = 0;

        validSpans.forEach((span) => {
            if (span.start > lastIndex) {
                jsxSegments.push(
                    <span key={`text-${lastIndex}`}>{text.substring(lastIndex, span.start)}</span>
                );
            }

            const highlightStyle = getHighlightStylesFromSeverity(span.severity);
            const isCurrentlyHovered = hoveredIndex === span.originalIndex;
            const isDimmed = hasFocus && !isCurrentlyHovered;
            
            jsxSegments.push(
                <Tooltip
                    key={`region-${span.originalIndex}`}
                    disableFocusListener
                    disableTouchListener={false}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    title={
                        <Box sx={{ p: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#F44336', mb: 1, letterSpacing: '0.5px' }}>
                                {t('industryApplications.interactive.textHighlight.severityLabel', { severity: span.severity })}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                                {span.reason}
                            </Typography>
                        </Box>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                        tooltip: { sx: { bgcolor: '#171F29', border: '1px solid #F44336', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', p: 1.5, maxWidth: 300 } },
                        arrow: { sx: { color: '#171F29' } }
                    }}
                >
                    <span
                        onMouseEnter={() => {
                            setHasFocus(true);
                            setHoveredIndex(span.originalIndex);
                        }}
                        onMouseLeave={() => {
                            setHasFocus(false);
                            setHoveredIndex(null);
                        }}
                        style={{
                            ...highlightStyle,
                            borderRadius: '4px',
                            padding: '2px 4px',
                            cursor: 'help',
                            transition: 'opacity 0.2s ease-in-out',
                            opacity: isDimmed ? 0.3 : 1,
                        }}
                    >
                        {text.substring(span.start, span.end)}
                    </span>
                </Tooltip>
            );
            lastIndex = span.end;
        });

        if (lastIndex < text.length) {
            jsxSegments.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
        }
        return jsxSegments;
    }, [text, regions, hasFocus, hoveredIndex, t]);

    return (
        <Box sx={{ 
            width: '100%', height: '100%', maxHeight: '350px', overflowY: 'auto', 
            backgroundColor: 'rgba(0,0,0,0.4)', p: 3, borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)', position: 'relative', 
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--secondary-color)', borderRadius: '4px' }
        }}>
            
            {infoPoint && (
                <Tooltip
                    placement={infoPoint.position || 'right'}
                    arrow
                    disableFocusListener
                    disableTouchListener={false}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    componentsProps={{
                        tooltip: { sx: { bgcolor: '#171F29', border: '1px solid var(--secondary-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', p: 1.5, maxWidth: 380 } },
                        arrow: { sx: { color: '#171F29' } }
                    }}
                    title={
                        <div style={{ textAlign: 'left', fontFamily: 'Roboto, sans-serif' }}>
                            <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '0.75rem', color: 'var(--secondary-color)', letterSpacing: '0.5px' }}>
                                {infoPoint.title}
                            </div>
                            <div style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.5 }}>
                                {infoPoint.reason}
                            </div>
                        </div>
                    }
                >
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.8, type: 'spring' }}
                        style={{ 
                            position: 'absolute', top: '20px', left: '20px', width: '26px', height: '26px', 
                            backgroundColor: 'var(--secondary-color)', border: '2px solid white', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', 
                            zIndex: 10, boxShadow: '0 4px 10px rgba(0,0,0,0.5)',
                        }}
                    >
                        <span style={{ color: 'white', fontSize: '15px', fontWeight: 'bold', fontFamily: 'serif', marginTop: '-1px' }}>i</span>
                    </motion.div>
                </Tooltip>
            )}

            {verifyUrl && shieldIcon && (
                <Tooltip
                    placement="left"
                    arrow
                    componentsProps={{
                        tooltip: { sx: { bgcolor: '#171F29', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', padding: '8px 12px', border: '1px solid rgba(255,255,255,0.1)' } },
                        arrow: { sx: { color: '#171F29' } }
                    }}
                    title={<div style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>{t('industryApplications.interactive.textHighlight.verifyTooltip')}</div>}
                >
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }} 
                        animate={{ scale: 1, opacity: 1 }} 
                        transition={{ delay: 1, type: 'spring' }}
                        onClick={() => window.open(verifyUrl, '_blank')}
                        style={{
                            position: 'absolute', top: '18px', right: '20px', cursor: 'pointer', zIndex: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => { 
                            e.currentTarget.style.transform = 'scale(1.1)'; 
                            e.currentTarget.style.filter = 'brightness(1.2)'; 
                        }}
                        onMouseLeave={(e) => { 
                            e.currentTarget.style.transform = 'scale(1)'; 
                            e.currentTarget.style.filter = 'none'; 
                        }}
                    >
                        <img 
                            src={shieldIcon} 
                            alt="Vetra Verification" 
                            style={{ width: '32px', height: 'auto', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }} 
                        />
                    </motion.div>
                </Tooltip>
            )}

            <Typography sx={{ 
                whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: 'Georgia, serif', fontSize: '1rem', 
                color: hasFocus ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.85)',
                transition: 'color 0.3s ease', paddingTop: (infoPoint || verifyUrl) ? '2.5rem' : '0' 
            }}>
                {segments.map((segment, index) => (
                    <React.Fragment key={index}>{segment}</React.Fragment>
                ))}
            </Typography>
        </Box>
    );
};

export default InteractiveTextHighlight;